/**
 * @file BlendMaterialModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-09-30
 * @description Add or edit one raw material.
 *
 *              The two links are the point of this screen. The ingredient
 *              bridge is what lets a blend be promoted to a product formulation
 *              — several materials can share one component, because the
 *              registry has a single "Maize %" where the workbook has four
 *              maize products. The inventory link is what lets a cost be pulled
 *              from Xero; it's left blank by the seed because matching
 *              "Rolled Barley Tote" against "Rolled barley in bulk" by name is
 *              a guess, and a wrong link quietly pulls the wrong price.
 *
 *              Aliases are the other half of the monthly paste working: every
 *              spelling a material appears under in a supplier's sheet.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import Textarea from './Textarea';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import './BlendCalculator.css';
import ItemAccountsPanel from './ItemAccountsPanel';

const CATEGORIES = [
  { value: '', label: 'No category' },
  { value: 'cereal', label: 'Cereal' },
  { value: 'protein', label: 'Protein' },
  { value: 'fibre', label: 'Fibre' },
  { value: 'mineral', label: 'Mineral' },
  { value: 'additive', label: 'Additive' },
  { value: 'liquid', label: 'Liquid' },
  { value: 'other', label: 'Other' }
];

function MaterialModalBody({
  form, setField, ingredientOptions, itemOptions, aliases, newAlias, setNewAlias,
  onAddAlias, addingAlias, error, isNew
}) {
  useSlideOutButton([addingAlias, isNew]);

  return (
    <div className="blend-material-body">
      {error && <div className="formulation-error">{error}</div>}

      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="bm-name">Name</label>
          <Input id="bm-name" type="text" value={form.name}
                 placeholder="As it should read on a spec sheet"
                 onChange={(e) => setField('name', e.target.value)} />
        </div>

        <div className="form-group">
          <label htmlFor="bm-category">Category</label>
          <Dropdown options={CATEGORIES} value={form.category || ''}
                    onChange={(v) => setField('category', v)} />
        </div>

        <div className="form-group">
          <label htmlFor="bm-supplier">Mill / supplier code</label>
          <Input id="bm-supplier" type="text" value={form.supplier_code || ''}
                 placeholder="e.g. 40001021"
                 onChange={(e) => setField('supplier_code', e.target.value)} />
        </div>

        <div className="form-group">
          <label>Active</label>
          <div className="checkbox-container">
            <div className="check light">
              <input id="bm-active" type="checkbox" checked={form.is_active !== false}
                     onChange={(e) => setField('is_active', e.target.checked)} />
              <label htmlFor="bm-active"></label>
            </div>
          </div>
        </div>
      </div>

      {/* The bridge in both directions: it lets a formulation be costed from raw
          material prices, and a blend be promoted to a product. Many-to-many —
          several materials can answer for one ingredient (promotion sums them),
          and one material can answer for several ingredients where the registry
          holds more than one spelling of it. */}
      <div className="form-group">
        <label>Ingredients it maps to on a formulation</label>

        {form.components.length > 0 && (
          <div className="blend-comp-list">
            {form.components.map((c) => (
              <span key={c.key} className={`blend-comp-chip${c.is_primary ? ' primary' : ''}`}>
                {c.label || c.key}
                {/* Which one promotion writes into. Only meaningful with more
                    than one, so the control only appears then. */}
                {form.components.length > 1 && (
                  <button type="button" className="blend-comp-star"
                          title={c.is_primary
                            ? 'Promoting a blend writes into this ingredient'
                            : 'Make this the one promotion writes into'}
                          onClick={() => setField('components', form.components.map((x) => ({
                            ...x, is_primary: x.key === c.key
                          })))}>
                    <span className="material-symbols-rounded">
                      {c.is_primary ? 'star' : 'star_border'}
                    </span>
                  </button>
                )}
                <button type="button" className="blend-comp-remove" title="Remove"
                        onClick={() => {
                          const left = form.components.filter((x) => x.key !== c.key);
                          // Never leave a set with no primary — promotion would
                          // have no target and the material would read as unmapped.
                          if (left.length && !left.some((x) => x.is_primary)) {
                            left[0] = { ...left[0], is_primary: true };
                          }
                          setField('components', left);
                        }}>
                  <span className="material-symbols-rounded">close</span>
                </button>
              </span>
            ))}
          </div>
        )}

        <SearchableDropdown
          options={ingredientOptions.filter(
            (o) => !form.components.some((c) => c.key === o.value))}
          value={null}
          onChange={(v) => {
            if (!v) return;
            const opt = ingredientOptions.find((o) => o.value === v);
            setField('components', [
              ...form.components,
              { key: v, label: opt?.label || v, is_primary: form.components.length === 0 }
            ]);
          }}
          placeholder={form.components.length ? 'Add another ingredient…' : 'Not mapped —'}
          searchPlaceholder="Search ingredients…"
        />
        <span className="blend-field-hint">
          Costing a formulation uses whichever of these has a price. Promoting a blend
          writes into the starred one — writing into all of them would count the
          inclusion twice. A blend containing an unmapped material can't be promoted.
        </span>
      </div>

      <div className="form-group">
        <label>Inventory item (for pulling cost from Xero)</label>
        <SearchableDropdown
          options={itemOptions}
          value={form.inventory_item_id}
          onChange={(v) => setField('inventory_item_id', v)}
          placeholder="Not linked"
          searchPlaceholder="Search inventory…"
        />
        <span className="blend-field-hint">
          Only link an item you actually buy as this material — a wrong link pulls the wrong cost.
        </span>
      </div>

      {/* The linked item's ledger side — read-only, and a quick check that the
          link is the product it looks like. */}
      <ItemAccountsPanel itemId={form.inventory_item_id} />

      <div className="form-group">
        <label htmlFor="bm-notes">Notes</label>
        <Textarea id="bm-notes" rows={2} value={form.notes || ''}
                  placeholder="Optional…"
                  onChange={(e) => setField('notes', e.target.value)} />
      </div>

      {!isNew && (
        <div className="form-group">
          <label>Alternative spellings</label>
          <span className="blend-field-hint">
            Every name this material appears under in a supplier's sheet. These are what make
            a pasted price block resolve on its own.
          </span>
          <div className="blend-alias-list">
            {aliases.map((a) => (
              <span className="blend-alias" key={a.id || a.alias}>{a.alias}</span>
            ))}
            {!aliases.length && <span className="blend-field-hint">None yet.</span>}
          </div>
          <div className="fm-add-component">
            <Input type="text" icon="add" value={newAlias} disabled={addingAlias}
                   placeholder="Add a spelling…"
                   onChange={(e) => setNewAlias(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddAlias(); } }} />
            <button type="button" className="btn-icon" onClick={onAddAlias}
                    disabled={addingAlias || !newAlias.trim()} title="Add this spelling">
              <span className={`material-symbols-rounded${addingAlias ? ' spinning' : ''}`}>
                {addingAlias ? 'progress_activity' : 'playlist_add'}
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BlendMaterialModal({ material, components = [], onClose, onSaved }) {
  const isNew = !material;
  const [form, setForm] = useState(() => ({
    name: material?.name || '',
    category: material?.category || '',
    supplier_code: material?.supplier_code || '',
    inventory_item_id: material?.inventory_item_id || null,
    // [{ key, label, is_primary }] — a material can answer for several
    // components (the registry holds two spellings of Wheatfeed).
    components: material?.components || [],
    notes: material?.notes || '',
    is_active: material?.is_active !== false
  }));
  const [aliases, setAliases] = useState(material?.aliases?.map((a) => ({ alias: a })) || []);
  const [newAlias, setNewAlias] = useState('');
  const [addingAlias, setAddingAlias] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const setField = useCallback((key, value) =>
    setForm((prev) => ({ ...prev, [key]: value })), []);

  // The ingredient registry and the inventory list both come from the
  // formulations API, which already exposes exactly these two lookups.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch(`${API_URL}/api/formulations/components`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { components: [] })),
      fetch(`${API_URL}/api/formulations/items?limit=2000`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { items: [] }))
    ]).then(([c, i]) => {
      if (cancelled) return;
      setIngredients((c.components || []).filter((x) => x.component_group === 'ingredients'));
      setItems(i.items || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const ingredientOptions = useMemo(() => [
    { value: null, label: 'Not mapped' },
    ...ingredients.map((c) => ({ value: c.key, label: c.label, searchText: `${c.label} ${c.key}` }))
  ], [ingredients]);

  const itemOptions = useMemo(() => [
    { value: null, label: 'Not linked' },
    ...items.map((i) => ({
      value: i.id,
      label: i.item_name,
      badge: i.item_code,
      searchText: `${i.item_name} ${i.item_code}`
    }))
  ], [items]);

  const addAlias = useCallback(async () => {
    const alias = newAlias.trim();
    if (!alias || !material?.id) return;
    setAddingAlias(true);
    try {
      const res = await fetch(`${API_URL}/api/blends/materials/${material.id}/aliases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ alias })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not add that spelling');
      setAliases((prev) => [...prev, { alias }]);
      setNewAlias('');
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingAlias(false);
    }
  }, [newAlias, material]);

  const save = useCallback(async () => {
    if (!form.name.trim()) { setError('A name is required'); return; }
    setSaving(true);
    setError('');
    try {
      const url = isNew
        ? `${API_URL}/api/blends/materials`
        : `${API_URL}/api/blends/materials/${material.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...form,
          category: form.category || null,
          supplier_code: form.supplier_code || null
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      /*
       * A SENTENCE, not the row.
       *
       * Both call sites do onSaved={(msg) => setMessage(msg)} and render that
       * message. Handing them the material object put an object into JSX, which
       * React refuses — error #31 — and the screen went down the instant a save
       * succeeded. The save had worked every time; the crash on the way back is
       * what made it look as though material mappings could not be saved.
       */
      onSaved(isNew
        ? `Added ${data.material?.name || form.name}.`
        : `Saved ${data.material?.name || form.name}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }, [form, isNew, material, onSaved]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isNew ? 'Add a raw material' : `Edit ${material.name}`}
      size="md"
      className="blend-material"
      footer={
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-round" onClick={save} disabled={saving || !form.name.trim()}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>
                  {saving ? 'progress_activity' : 'save'}
                </span>
              </div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      <MaterialModalBody
        form={form}
        setField={setField}
        ingredientOptions={ingredientOptions}
        itemOptions={itemOptions}
        aliases={aliases}
        newAlias={newAlias}
        setNewAlias={setNewAlias}
        onAddAlias={addAlias}
        addingAlias={addingAlias}
        error={error}
        isNew={isNew}
      />
    </Modal>
  );
}
